import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import companyService from '../../services/companyService';

// ── Animations ──

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// ── Layout ──

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  background: #0a0a14;
`;

const Card = styled.div`
  width: 100%;
  max-width: 480px;
  background: #10101c;
  border: 1px solid #2a2a44;
  border-radius: 16px;
  padding: 40px 32px;
  animation: ${fadeIn} 0.5s ease-out;

  @media (max-width: 520px) {
    padding: 32px 20px;
  }
`;

const HeaderBar = styled.div`
  height: 3px;
  width: 48px;
  background: linear-gradient(90deg, #6366F1, #8B5CF6, #EC4899);
  border-radius: 2px;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: #e0e0ec;
  margin: 0 0 8px 0;
`;

const Description = styled.p`
  font-size: 0.88rem;
  color: #8888a8;
  line-height: 1.6;
  margin: 0 0 32px 0;
`;

// ── Form Elements ──

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: #e0e0ec;
  margin-bottom: 8px;
`;

const RequiredMark = styled.span`
  color: #EC4899;
  margin-left: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: #181830;
  border: 1px solid #2a2a44;
  border-radius: 10px;
  color: #e0e0ec;
  font-size: 0.92rem;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;

  &::placeholder {
    color: #555570;
  }

  &:focus {
    border-color: #6366F1;
  }
`;

const InviteCodeInput = styled(Input)`
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 1.1rem;
  letter-spacing: 4px;
  text-transform: uppercase;
  text-align: center;
`;

const HelperText = styled.p`
  font-size: 0.76rem;
  color: #8888a8;
  margin: 8px 0 0 0;
  line-height: 1.5;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #6366F1, #8B5CF6);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.1s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 24px;
  font-size: 0.84rem;
  color: #f87171;
  line-height: 1.5;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  background: #181830;
  border: 1px solid #2a2a44;
  border-radius: 10px;
  color: #e0e0ec;
  font-size: 0.92rem;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23555570' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;

  &:focus {
    border-color: #6366F1;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const OptionalMark = styled.span`
  color: #8888a8;
  font-weight: 400;
  margin-left: 4px;
  font-size: 0.75rem;
`;

const CompanyNameBanner = styled.div`
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 24px;
  font-size: 0.92rem;
  color: #a5b4fc;
  font-weight: 600;
  text-align: center;
`;

const PreviewSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(99, 102, 241, 0.3);
  border-top-color: #6366F1;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
  display: inline-block;
  margin-left: 8px;
  vertical-align: middle;
`;

// ── Unauthenticated State ──

const AuthPrompt = styled.div`
  text-align: center;
`;

const AuthMessage = styled.p`
  font-size: 0.9rem;
  color: #8888a8;
  margin: 0 0 24px 0;
  line-height: 1.6;
`;

const LoginButton = styled.button`
  padding: 12px 32px;
  background: #6366F1;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #8B5CF6;
  }
`;

// ── Component ──

const CompanyJoinPage = () => {
  const { t } = useLanguage();
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();

  const [inviteCode, setInviteCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Preview state
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewCodeRef = useRef('');

  // Selection state
  const [selectedDeptId, setSelectedDeptId] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('');

  // Filtered teams based on selected department
  const filteredTeams = preview && selectedDeptId
    ? (preview.departments.find(d => d.id === selectedDeptId)?.teams || [])
    : [];

  // Auto-fetch preview when invite code reaches 8 characters
  const fetchPreview = useCallback(async (code) => {
    if (code.length !== 8) {
      setPreview(null);
      return;
    }
    // Avoid duplicate fetches
    if (previewCodeRef.current === code) return;
    previewCodeRef.current = code;

    setPreviewLoading(true);
    setError(null);
    try {
      const data = await companyService.getJoinPreview(code);
      setPreview(data);
      setSelectedDeptId('');
      setSelectedTeamId('');
      setSelectedPosition('');
    } catch (err) {
      setPreview(null);
      if (err.status === 404) {
        setError(
          t(
            '招待コードが無効です。コードを確認してもう一度お試しください。',
            'Invalid invite code. Please check the code and try again.'
          )
        );
      }
    } finally {
      setPreviewLoading(false);
    }
  }, [t]);

  const handleInviteCodeChange = (e) => {
    const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    if (value.length <= 8) {
      setInviteCode(value);
      if (value.length === 8) {
        fetchPreview(value);
      } else {
        setPreview(null);
        previewCodeRef.current = '';
      }
    }
  };

  // Reset team when department changes
  useEffect(() => {
    setSelectedTeamId('');
  }, [selectedDeptId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const trimmedCode = inviteCode.trim().toUpperCase();
    const trimmedName = displayName.trim();

    if (!trimmedCode) {
      setError(t('招待コードを入力してください。', 'Please enter an invite code.'));
      return;
    }

    if (trimmedCode.length !== 8) {
      setError(
        t(
          '招待コードは8文字です。正しいコードを入力してください。',
          'Invite code must be 8 characters. Please enter a valid code.'
        )
      );
      return;
    }

    if (!trimmedName) {
      setError(t('表示名を入力してください。', 'Please enter a display name.'));
      return;
    }

    setLoading(true);
    try {
      const result = await companyService.joinCompany(
        trimmedCode,
        trimmedName,
        {
          departmentId: selectedDeptId || null,
          teamId: selectedTeamId || null,
          position: selectedPosition || null,
        }
      );
      navigate(`/company/${result.slug}`);
    } catch (err) {
      if (err.status === 404) {
        setError(
          t(
            '招待コードが無効です。コードを確認してもう一度お試しください。',
            'Invalid invite code. Please check the code and try again.'
          )
        );
      } else if (err.status === 409) {
        setError(
          t(
            'すでにこの組織のメンバーです。',
            'You are already a member of this organization.'
          )
        );
      } else {
        setError(
          err.message ||
            t(
              '組織への参加に失敗しました。しばらくしてからお試しください。',
              'Failed to join organization. Please try again later.'
            )
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = inviteCode.trim().length === 8 && displayName.trim().length > 0;

  // ── Unauthenticated View ──

  if (!isAuthenticated) {
    return (
      <PageWrapper>
        <Card>
          <HeaderBar />
          <Title>{t('組織に参加', 'Join Organization')}</Title>
          <AuthPrompt>
            <AuthMessage>
              {t(
                '組織に参加するにはログインが必要です。',
                'Please sign in to join an organization.'
              )}
            </AuthMessage>
            <LoginButton onClick={login}>
              {t('ログイン', 'Sign In')}
            </LoginButton>
          </AuthPrompt>
        </Card>
      </PageWrapper>
    );
  }

  // ── Authenticated View ──

  return (
    <PageWrapper>
      <Card>
        <HeaderBar />
        <Title>{t('組織に参加', 'Join Organization')}</Title>
        <Description>
          {t(
            '管理者から共有された招待コードを入力して、組織に参加します。',
            'Enter the invite code shared by your administrator to join an organization.'
          )}
        </Description>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>
              {t('招待コード', 'Invite Code')}
              <RequiredMark>*</RequiredMark>
            </Label>
            <InviteCodeInput
              type="text"
              value={inviteCode}
              onChange={handleInviteCodeChange}
              placeholder="ABCD1234"
              disabled={loading}
              maxLength={8}
              autoFocus
              autoComplete="off"
              spellCheck="false"
            />
            <HelperText>
              {t(
                '8文字の英数字コードです。管理者にお問い合わせください。',
                '8-character alphanumeric code. Contact your administrator if you don\'t have one.'
              )}
              {previewLoading && <PreviewSpinner />}
            </HelperText>
          </FormGroup>

          {preview && (
            <CompanyNameBanner>
              {preview.company_name}
            </CompanyNameBanner>
          )}

          <FormGroup>
            <Label>
              {t('表示名', 'Display Name')}
              <RequiredMark>*</RequiredMark>
            </Label>
            <Input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t('例: 田中 太郎', 'e.g. John Smith')}
              disabled={loading}
            />
            <HelperText>
              {t(
                '組織内で表示される名前です。後から変更できます。',
                'This name will be displayed within the organization. You can change it later.'
              )}
            </HelperText>
          </FormGroup>

          {preview && preview.departments.length > 0 && (
            <FormGroup>
              <Label>
                {t('部署', 'Department')}
                <OptionalMark>{t('任意', 'optional')}</OptionalMark>
              </Label>
              <Select
                value={selectedDeptId}
                onChange={(e) => setSelectedDeptId(e.target.value)}
                disabled={loading}
              >
                <option value="">{t('選択しない', 'Not selected')}</option>
                {preview.departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </Select>
            </FormGroup>
          )}

          {preview && selectedDeptId && filteredTeams.length > 0 && (
            <FormGroup>
              <Label>
                {t('チーム', 'Team')}
                <OptionalMark>{t('任意', 'optional')}</OptionalMark>
              </Label>
              <Select
                value={selectedTeamId}
                onChange={(e) => setSelectedTeamId(e.target.value)}
                disabled={loading}
              >
                <option value="">{t('選択しない', 'Not selected')}</option>
                {filteredTeams.map((team) => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </Select>
            </FormGroup>
          )}

          {preview && preview.positions.length > 0 && (
            <FormGroup>
              <Label>
                {t('役職', 'Position')}
                <OptionalMark>{t('任意', 'optional')}</OptionalMark>
              </Label>
              <Select
                value={selectedPosition}
                onChange={(e) => setSelectedPosition(e.target.value)}
                disabled={loading}
              >
                <option value="">{t('選択しない', 'Not selected')}</option>
                {preview.positions.map((pos, idx) => (
                  <option key={idx} value={pos}>{pos}</option>
                ))}
              </Select>
            </FormGroup>
          )}

          <SubmitButton type="submit" disabled={loading || !isFormValid}>
            {loading ? (
              <>
                <Spinner />
                {t('参加中...', 'Joining...')}
              </>
            ) : (
              t('組織に参加する', 'Join Organization')
            )}
          </SubmitButton>
        </form>
      </Card>
    </PageWrapper>
  );
};

export default CompanyJoinPage;
